import React, { useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  LocationOn as LocationIcon,
  TripOrigin as TripOriginIcon,
} from '@mui/icons-material';

/**
 * Routenpunkte wie bei Google Maps: Abholung → Zwischenstopps → Ziel,
 * gemeinsam genutzt von RideForm (großer Dialog) und QuickRideForm (LiveMap).
 *
 * - Drag & Drop über den Griff (Ziel nach oben = Start und Ziel tauschen)
 * - Tastatur über `useRoutePointShortcuts`: Alt+Z / Alt+Entf / Alt+↑↓
 *
 * Die Felder bleiben an ihrer Position (erstes = Abholung, letztes = Ziel,
 * dazwischen `stop-<i>`), damit die Tastaturkette stabil bleibt; beim
 * Umsortieren wandern nur die Werte samt Koordinaten. Die Felder selbst rendert
 * das jeweilige Formular (`renderField`), weil sich deren State unterscheidet.
 *
 * Siehe zwischenstopps_konzept.md §3.2 und TASTATURGEFUEHRTE_FAHRTERFASSUNG.md 3.7.
 */

// Feldname der Tastaturführung je Position.
export const routeNavName = (index, count) => {
  if (index <= 0) return 'pickupAddress';
  if (index >= count - 1) return 'destinationAddress';
  return `stop-${index - 1}`;
};

export const routeIndexFromNav = (navName, count) => {
  if (navName === 'pickupAddress') return 0;
  if (navName === 'destinationAddress') return count - 1;
  const match = /^stop-(\d+)$/.exec(navName || '');
  return match ? Number(match[1]) + 1 : null;
};

// Einen Punkt aus der Liste [Abholung, ...Stopps, Ziel] an eine neue Position.
export const reorderRoutePoints = (points, fromIndex, toIndex) => {
  if (fromIndex === toIndex) return points;
  if (fromIndex < 0 || fromIndex >= points.length || toIndex < 0 || toIndex >= points.length) {
    return points;
  }
  const next = [...points];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

/**
 * Tastaturkürzel für die Routenpunkte, als `altShortcuts` für
 * useFormKeyboardNav. `setFocusField` muss nach dem Hook-Aufruf mit dessen
 * `focusField` versorgt werden (Henne-Ei: die Kürzel gehen in den Hook hinein).
 *
 * @param {object} options
 * @param {boolean} options.stopsAllowed  Zwischenstopps erlaubt (nicht bei Krankenfahrt/Dauerauftrag)
 * @param {number}  options.stopsCount    aktuelle Anzahl Zwischenstopps
 * @param {number}  options.maxStops
 * @param {Function} options.onAddStop    hängt einen leeren Stopp an (vor dem Ziel)
 * @param {Function} options.onRemoveStop (index)
 * @param {Function} options.onReorder    (fromIndex, toIndex)
 */
export const useRoutePointShortcuts = ({
  stopsAllowed,
  stopsCount,
  maxStops,
  onAddStop,
  onRemoveStop,
  onReorder,
}) => {
  const focusFieldRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const pointCount = 2 + (stopsAllowed ? stopsCount : 0);

  // Fokus erst setzen, wenn das Feld gerendert ist. Ein neues Adressfeld baut
  // sein Google-Element erst im Effect auf – deshalb ein paar Versuche.
  const focusLater = (navName) => {
    pendingFocusRef.current = navName;
    let attempts = 0;
    const tryFocus = () => {
      if (pendingFocusRef.current !== navName) return;
      if (focusFieldRef.current?.(navName) || attempts >= 12) {
        pendingFocusRef.current = null;
        return;
      }
      attempts += 1;
      setTimeout(tryFocus, 50);
    };
    setTimeout(tryFocus, 0);
  };

  const addStop = ({ focus = true } = {}) => {
    if (!stopsAllowed || stopsCount >= maxStops) return;
    onAddStop();
    if (focus) focusLater(`stop-${stopsCount}`);
  };

  const removeStop = (stopIndex, { focus = false } = {}) => {
    onRemoveStop(stopIndex);
    if (focus) focusLater(stopIndex === 0 ? 'pickupAddress' : `stop-${stopIndex - 1}`);
  };

  const moveFrom = (navElement, direction) => {
    const from = routeIndexFromNav(navElement?.dataset?.nav, pointCount);
    if (from === null) return false; // kein Adressfeld: Taste normal behandeln
    // Offene Vorschlagsliste: ↑/↓ gehören Google.
    if (navElement.dataset.navOpen === 'true') return false;
    const to = from + direction;
    if (to < 0 || to >= pointCount) return true; // Rand erreicht – nichts tun
    onReorder(from, to);
    focusLater(routeNavName(to, pointCount));
    return true;
  };

  const shortcuts = {
    // Zwischenstopp vor dem Ziel einfügen, Cursor hinein
    z: () => addStop(),
    // Zwischenstopp entfernen, in dem der Cursor steht
    delete: (navElement) => {
      const match = /^stop-(\d+)$/.exec(navElement?.dataset?.nav || '');
      if (!match) return false;
      removeStop(Number(match[1]), { focus: true });
      return true;
    },
    // Routenpunkt verschieben (Tastatur-Gegenstück zum Drag & Drop)
    arrowup: (navElement) => moveFrom(navElement, -1),
    arrowdown: (navElement) => moveFrom(navElement, 1),
  };

  return {
    shortcuts,
    addStop,
    removeStop,
    setFocusField: (focusField) => { focusFieldRef.current = focusField; },
  };
};

const RouteMarker = ({ kind, index }) => {
  if (kind === 'pickup') {
    return <TripOriginIcon fontSize="small" sx={{ color: 'success.main' }} titleAccess="Abholung" />;
  }
  if (kind === 'destination') {
    return <LocationIcon fontSize="small" sx={{ color: 'error.main' }} titleAccess="Ziel" />;
  }
  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: 'text.secondary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.65rem',
        fontWeight: 700,
        color: 'text.secondary',
      }}
    >
      {index + 1}
    </Box>
  );
};

/**
 * @param {object} props
 * @param {number}   props.stopsCount   Anzahl sichtbarer Zwischenstopps
 * @param {Function} props.renderField  ({ kind: 'pickup'|'stop'|'destination', index? }) => ReactNode
 * @param {Function} props.onReorder    (fromIndex, toIndex)
 * @param {Function} props.onRemoveStop (index)
 * @param {ReactNode} [props.destinationAction] optional rechts neben dem Zielfeld
 *   (QuickRideForm: kompaktes „+" statt eines eigenen Buttons darunter)
 */
const RoutePointsEditor = ({ stopsCount, renderField, onReorder, onRemoveStop, destinationAction = null }) => {
  // Ziehen per Pointer Events statt HTML5-Drag-&-Drop: Das kennt auf
  // Touch-Geräten weder Maus-Events zum Scharfschalten noch (Android) ein
  // verlässliches `draggable`. Pointer Events decken Maus, Finger und Stift ab.
  // Gezogen wird nur am Griff, die Textauswahl in den Adressfeldern bleibt frei.
  const rowRefs = useRef([]);
  const dragRef = useRef({ from: null, over: null, pointerId: null });
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const resetDrag = () => {
    dragRef.current = { from: null, over: null, pointerId: null };
    setDragFrom(null);
    setDragOver(null);
  };

  // Zeile unter der Fingerposition; oberhalb der ersten bzw. unterhalb der
  // letzten Zeile zählt der jeweilige Rand.
  const rowIndexAt = (clientY) => {
    const elements = rowRefs.current.slice(0, stopsCount + 2);
    for (let i = 0; i < elements.length; i += 1) {
      const rect = elements[i]?.getBoundingClientRect();
      if (rect && clientY < rect.bottom) return i;
    }
    return elements.length - 1;
  };

  const handlePointerDown = (rowIndex) => (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault(); // kein Fokuswechsel, keine Textauswahl
    // Folgebewegungen kommen am Griff an, auch wenn der Zeiger ihn verlässt.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* Zeiger schon weg */ }
    dragRef.current = { from: rowIndex, over: rowIndex, pointerId: e.pointerId };
    setDragFrom(rowIndex);
    setDragOver(rowIndex);
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (drag.from === null || e.pointerId !== drag.pointerId) return;
    const over = rowIndexAt(e.clientY);
    if (over !== drag.over) {
      drag.over = over;
      setDragOver(over);
    }
  };

  const handlePointerUp = (e) => {
    const { from, over, pointerId } = dragRef.current;
    if (from === null || e.pointerId !== pointerId) return;
    resetDrag();
    if (over !== null && over !== from) onReorder(from, over);
  };

  // Rechte Spalte (✕ / „+") nur, wenn es darin etwas gibt – mobil zählt jeder Pixel.
  const hasActionColumn = stopsCount > 0 || !!destinationAction;

  const rows = [
    { key: 'pickup', kind: 'pickup' },
    ...Array.from({ length: stopsCount }, (_, index) => ({ key: `stop-${index}`, kind: 'stop', index })),
    { key: 'destination', kind: 'destination' },
  ];

  return (
    <Box role="list" aria-label="Routenpunkte" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rows.map((row, rowIndex) => {
        const dragging = dragFrom !== null;
        const dropBefore = dragging && dragOver === rowIndex && dragFrom > rowIndex;
        const dropAfter = dragging && dragOver === rowIndex && dragFrom < rowIndex;
        return (
          <Box
            key={row.key}
            ref={(element) => { rowRefs.current[rowIndex] = element; }}
            role="listitem"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              borderRadius: 1,
              opacity: dragFrom === rowIndex ? 0.4 : 1,
              boxShadow: (theme) => (dropBefore
                ? `0 -3px 0 0 ${theme.palette.primary.main}`
                : dropAfter
                  ? `0 3px 0 0 ${theme.palette.primary.main}`
                  : 'none'),
            }}
          >
            <Tooltip
              title="Ziehen, um die Reihenfolge zu ändern (Tastatur: Alt+↑ / Alt+↓)"
              enterDelay={600}
              disableTouchListener
            >
              <Box
                aria-hidden
                onPointerDown={handlePointerDown(rowIndex)}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={resetDrag}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'stretch',
                  minWidth: 24,
                  cursor: dragging ? 'grabbing' : 'grab',
                  color: dragFrom === rowIndex ? 'primary.main' : 'text.disabled',
                  // Finger zieht die Zeile statt die Seite zu scrollen; iOS
                  // soll beim langen Drücken kein Kontextmenü/Markieren zeigen.
                  touchAction: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  '&:hover': { color: 'text.secondary' },
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
            </Tooltip>
            <Box sx={{ width: 20, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <RouteMarker kind={row.kind} index={row.index} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {renderField(row)}
            </Box>
            <Box sx={{ width: 34, display: hasActionColumn ? 'flex' : 'none', justifyContent: 'center', flexShrink: 0 }}>
              {row.kind === 'stop' && (
                <Tooltip title="Zwischenstopp entfernen (Alt+Entf)">
                  <IconButton size="small" tabIndex={-1} onClick={() => onRemoveStop(row.index)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {row.kind === 'destination' && destinationAction}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default RoutePointsEditor;
